import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import {
  ShieldAlert,
  FileCode,
  ArrowRight,
  Search,
  Activity,
  AlertTriangle,
} from "lucide-react";

const compactLoc = (n) => {
  if (!n) return "0";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return String(n);
};

export default function Overview() {
  const [kpis, setKpis] = useState(null);
  const [workspace, setWorkspace] = useState(null);

  useEffect(() => {
    (async () => {
      const [k, w] = await Promise.all([
        api.get("/api/overview/kpis"),
        api.get("/api/workspace"),
      ]);
      setKpis(k.data);
      setWorkspace(w.data);
    })();
  }, []);

  return (
    <div className="p-6 md:p-10 space-y-8">
      {/* Header block */}
      <div className="relative overflow-hidden border border-[color:var(--border)] p-8 md:p-10">
        <div
          className="absolute inset-0 grid-bg opacity-30 pointer-events-none"
          aria-hidden
        />
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(500px circle at 90% 0%, rgba(0,240,255,0.12), transparent 60%)",
          }}
        />
        <div className="relative z-10 max-w-3xl">
          <div className="eyebrow">/ workspace · {workspace?.name || "…"}</div>
          <h1 className="mt-3 text-4xl md:text-5xl font-medium tracking-tight leading-[1.05]">
            Legacy modernization
            <br />
            <span className="text-[color:var(--accent)]">under one runway.</span>
          </h1>
          <p className="mt-4 text-[color:var(--text-2)] max-w-xl">
            Revora runs the 5-stage assessment engine over your client's
            estate — scan the code, score the risk, prescribe the treatment,
            track the migration, and keep it from decaying back into legacy.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              data-testid="cta-run-scan"
              to="/scan"
              className="inline-flex items-center gap-2 bg-[color:var(--accent)] text-black h-11 px-5 hover:bg-[color:var(--accent-hover)] transition-colors duration-150 mono text-xs uppercase tracking-widest"
            >
              <Search size={14} /> Run a Scan
            </Link>
            <Link
              to="/diagnose"
              className="inline-flex items-center gap-2 border border-[color:var(--border-strong)] h-11 px-5 hover:border-white transition-colors duration-150 mono text-xs uppercase tracking-widest"
            >
              Open Diagnose <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Kpi
          icon={<Search size={16} />}
          label="Systems in scope"
          value={kpis?.systems ?? "—"}
          hint={
            kpis?.total_files
              ? `${kpis.total_files.toLocaleString()} files scanned`
              : "run a scan"
          }
          testId="kpi-systems"
        />
        <Kpi
          icon={<ShieldAlert size={16} />}
          label="Avg risk score"
          value={kpis?.diagnosed ? kpis.avg_risk : "—"}
          hint={kpis?.diagnosed ? "0–100 · higher = riskier" : "run diagnose"}
          accent={kpis?.diagnosed && kpis?.avg_risk >= 65}
          testId="kpi-risk"
        />
        <Kpi
          icon={<Activity size={16} />}
          label="Critical systems"
          value={kpis?.critical_count ?? "—"}
          hint={kpis?.diagnosed ? "risk ≥ 80 or tier-1" : "post-diagnose"}
          testId="kpi-critical"
        />
        <Kpi
          icon={<FileCode size={16} />}
          label="Lines of code"
          value={kpis ? compactLoc(kpis.total_loc) : "—"}
          hint={
            kpis?.findings_count
              ? `${kpis.findings_count} findings raised`
              : "measured from scan"
          }
          testId="kpi-loc"
        />
      </div>

      {/* Stage grid */}
      <div>
        <div className="eyebrow mb-4">/ the revora cycle</div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
          {[
            {
              code: "01",
              name: "Scan",
              to: "/scan",
              blurb: "Auto-crawls code, data, infra, live traffic.",
            },
            {
              code: "02",
              name: "Diagnose",
              to: "/diagnose",
              blurb: "Security × debt × criticality × compliance.",
            },
            {
              code: "03",
              name: "Prescribe",
              to: "/prescribe",
              blurb: "Wrap · Rewrite · Replatform + ROI + roadmap.",
            },
            {
              code: "04",
              name: "Transform",
              to: "/transform",
              blurb: "Kanban migration board, service by service.",
            },
            {
              code: "05",
              name: "Sustain",
              to: "/sustain",
              blurb: "Cost, drift, security posture governance.",
            },
          ].map((s) => (
            <Link
              key={s.code}
              to={s.to}
              data-testid={`stage-${s.name.toLowerCase()}`}
              className="border border-[color:var(--border)] bg-[color:var(--bg-1)] p-5 hover:border-[color:var(--accent)] transition-colors duration-150 group"
            >
              <div className="flex items-center justify-between">
                <span className="mono text-xs text-[color:var(--accent)]">
                  {s.code}
                </span>
                <ArrowRight
                  size={14}
                  className="text-[color:var(--text-3)] group-hover:text-[color:var(--accent)] transition-colors duration-150"
                />
              </div>
              <div className="mt-4 text-lg font-medium tracking-tight">
                {s.name}
              </div>
              <div className="mt-1 text-sm text-[color:var(--text-2)]">
                {s.blurb}
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

function Kpi({ icon, label, value, hint, accent = false, testId }) {
  return (
    <div
      data-testid={testId}
      className="border border-[color:var(--border)] bg-[color:var(--bg-1)] p-5"
    >
      <div className="flex items-center justify-between text-[color:var(--text-2)]">
        <span className="eyebrow">{label}</span>
        <span className={accent ? "text-[color:var(--critical)]" : ""}>
          {icon}
        </span>
      </div>
      <div
        className={`mt-3 text-3xl font-medium tracking-tight mono ${
          accent ? "text-[color:var(--critical)]" : "text-white"
        }`}
      >
        {value}
      </div>
      <div className="mt-1 mono text-[10px] uppercase tracking-widest text-[color:var(--text-3)]">
        {hint}
      </div>
    </div>
  );
}
